import { useState, useEffect, useCallback } from "react";
import Lenis from "lenis";
import { motion } from "framer-motion";
import "@/App.css";
import { Navbar } from "@/components/gmh/Navbar";
import { ShipHud } from "@/components/gmh/ShipHud";
import { Leaderboard } from "@/components/gmh/Leaderboard";
import { PodiumStrip } from "@/components/gmh/PodiumStrip";
import { CopyBlock } from "@/components/gmh/CopyBlock";
import { UrlLaunchBar } from "@/components/gmh/UrlLaunchBar";
import { BidOverlay } from "@/components/gmh/BidOverlay";
import { Hero } from "@/components/gmh/Hero";
import { LiveFeed } from "@/components/gmh/LiveFeed";
import { Protocol } from "@/components/gmh/Protocol";
import Footer from "@/components/gmh/Footer";
import { LegalPage, readLegalSlug } from "@/components/gmh/LegalPage";
import { Confirmation } from "@/components/gmh/Confirmation";
import { INITIAL_LISTINGS, SYSTEM_ALERTS, SAM_ACTIVITIES } from "@/components/gmh/data";
import {
  sortListings,
  bumpClicks,
  parseSocialProfile,
  previewLabels,
  isGenericSocialTitle,
  needsSocialMetaUpgrade,
} from "@/components/gmh/listings";
import { sound } from "@/components/gmh/sound";
import { api } from "@/api";
import { readCheckoutReturnId, stripCheckoutReturnParams, stripJunkParams } from "@/lib/cleanup";
import { readListingDeepLink } from "@/components/gmh/shareLinks";
import { recordVisitOnce, utcDay } from "@/components/gmh/visitOnce";
import { startPresenceHeartbeat } from "@/components/gmh/presence";
import { takeAmountForRank } from "@/components/gmh/rankForAmount";
import "@/apiConfirm";

function App() {
  const [total, setTotal] = useState(0);
  const [listings, setListings] = useState(INITIAL_LISTINGS);
  const leaderboard = sortListings(listings);
  const [fuelFeed, setFuelFeed] = useState([]);
  const [trafficFeed, setTrafficFeed] = useState([]);
  const [shipFeed, setShipFeed] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [boost, setBoost] = useState(false);
  const [musicOn, setMusicOn] = useState(false);
  const [theme, setTheme] = useState("space");
  const [confirmId, setConfirmId] = useState(() => readCheckoutReturnId(window.location.href));
  const [legalPage, setLegalPage] = useState(() => readLegalSlug(window.location.hash));
  const [visitors, setVisitors] = useState(null);
  const [online, setOnline] = useState(null);
  const [bidError, setBidError] = useState("");
  const [bidBusy, setBidBusy] = useState(false);
  const [prefillAmount, setPrefillAmount] = useState(0);
  const [prefillUrl, setPrefillUrl] = useState("");
  const production = api.isProduction();

  useEffect(() => {
    const sync = () => setLegalPage(readLegalSlug(window.location.hash));
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);

  useEffect(() => {
    try {
      if (localStorage.getItem("gmh-music-on") === "1") {
        setMusicOn(true);
      }
    } catch {
      /* storage may be unavailable */
    }
  }, []);

  useEffect(() => {
    if (!musicOn) return;
    if (sound.musicOn()) return;
    const start = (e) => {
      if (e.target && e.target.closest && e.target.closest('[data-testid="music-toggle-btn"]')) {
        return;
      }
      if (!sound.musicOn()) sound.toggleMusic();
      window.removeEventListener("click", start);
      window.removeEventListener("touchstart", start);
    };
    window.addEventListener("click", start, { once: false });
    window.addEventListener("touchstart", start, { once: false });
    return () => {
      window.removeEventListener("click", start);
      window.removeEventListener("touchstart", start);
    };
  }, [musicOn]);

  const toggleMusic = () => {
    sound.toggleMusic();
    const on = sound.musicOn();
    setMusicOn(on);
    try {
      localStorage.setItem("gmh-music-on", on ? "1" : "0");
    } catch {
      /* storage may be unavailable */
    }
  };

  useEffect(() => {
    const rootEl = document.documentElement;
    rootEl.classList.toggle("theme-light", theme === "light");
    if (theme === "light") rootEl.setAttribute("data-theme", "light");
    else rootEl.setAttribute("data-theme", "space");
    return () => {
      rootEl.classList.remove("theme-light");
      rootEl.removeAttribute("data-theme");
    };
  }, [theme]);

  useEffect(() => {
    if (legalPage) return undefined;
    const lenis = new Lenis({ lerp: 0.09 });
    const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, [legalPage]);

  const [boardState, setBoardState] = useState("loading");
  const [boardError, setBoardError] = useState("");

  const refreshBoard = useCallback(() => {
    let alive = true;
    setBoardState("loading");
    setBoardError("");
    api
      .getBoard()
      .then((board) => {
        if (!alive) return;
        setListings(board.listings);
        setTotal(board.total_cents / 100);
        setBoardState("ready");
        if (board.listings.length) {
          board.listings
            .filter((l) => needsSocialMetaUpgrade(l))
            .forEach((l) => {
              api
                .fetchPreview(l.url)
                .then((meta) => {
                  if (!alive || !meta || !meta.ok) return;
                  const labels = previewLabels(l.url, meta);
                  setListings((ls) =>
                    ls.map((x) => {
                      if (x.id !== l.id) return x;
                      const profile = parseSocialProfile(x.url);
                      const staleTitle =
                        profile &&
                        (isGenericSocialTitle(x.title, profile.platform) ||
                          x.title === `@${profile.handle}`);
                      const staleDesc =
                        !x.description ||
                        x.description === `Instagram profile @${profile.handle}` ||
                        x.description === `X profile @${profile.handle}` ||
                        x.description === `TikTok profile @${profile.handle}` ||
                        x.description === `YouTube profile @${profile.handle}`;
                      const next = { ...x };
                      if (staleDesc && labels.description) next.description = labels.description;
                      if (staleTitle && labels.name && labels.name !== x.title) next.title = labels.name;
                      if (!x.image && meta.image) next.image = meta.image;
                      return next;
                    })
                  );
                })
                .catch(() => {});
            });
        }
      })
      .catch((err) => {
        if (!alive) return;
        setBoardState("error");
        setBoardError(err && err.message ? err.message : "uplink unreachable");
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (legalPage) return undefined;
    api.prewarmHealth();
  }, [legalPage]);

  useEffect(() => {
    if (legalPage) return undefined;
    let alive = true;
    (async () => {
      try {
        await recordVisitOnce({
          getDay: utcDay,
          storage: window.localStorage,
          recordVisit: () => api.recordVisit(),
        });
      } catch {
        /* counted:false is handled inside recordVisitOnce */
      }
      try {
        const stats = await api.getStats();
        if (alive) {
          setVisitors(stats.visitors);
          if (typeof stats.online === "number") setOnline(stats.online);
        }
      } catch {
        if (alive) setVisitors(null);
      }
    })();
    return () => {
      alive = false;
    };
  }, [legalPage]);

  useEffect(() => {
    if (legalPage) return undefined;
    const stop = startPresenceHeartbeat({
      beat: (id) => api.heartbeat(id),
      onCount: (n) => setOnline(n),
    });
    return stop;
  }, [legalPage]);

  useEffect(() => {
    if (legalPage) return undefined;
    return refreshBoard();
  }, [refreshBoard, legalPage]);

  useEffect(() => {
    if (confirmId) return;
    const clean = stripJunkParams(window.location.href);
    if (clean.changed) {
      try {
        window.history.replaceState({}, document.title, clean.url);
      } catch {
        /* replaceState is best-effort */
      }
    }
  }, [confirmId]);

  const dismissCheckoutReturn = () => {
    const clean = stripCheckoutReturnParams(window.location.href);
    if (clean.changed) {
      try {
        window.history.replaceState({}, document.title, clean.url);
      } catch {
        /* best-effort */
      }
    }
    try {
      if (sessionStorage.getItem("gmh-just-paid") === "1") {
        sessionStorage.removeItem("gmh-just-paid");
        sound.bidBlast();
        setBoost(true);
        setTimeout(() => setBoost(false), 1800);
      }
    } catch {
      /* storage may be unavailable; boost is cosmetic */
    }
    setConfirmId(null);
    refreshBoard();
  };

  useEffect(() => {
    if (legalPage) return undefined;
    let alive = true;
    (async () => {
      try {
        const data = await api.getFeed();
        if (alive) {
          setFuelFeed(Array.isArray(data.fuel) ? data.fuel : []);
          setTrafficFeed(Array.isArray(data.traffic) ? data.traffic : []);
        }
      } catch {
        /* API asleep or unavailable — zones stay empty (honest). */
      }
    })();
    return () => {
      alive = false;
    };
  }, [legalPage]);

  useEffect(() => {
    if (legalPage || !SYSTEM_ALERTS.length) return;
    const i = setInterval(() => {
      const alert = SYSTEM_ALERTS[Math.floor(Math.random() * SYSTEM_ALERTS.length)];
      setShipFeed((f) => [{ ...alert, id: `sys-${Date.now()}`, ts: Date.now() }, ...f].slice(0, 20));
    }, 18000);
    return () => clearInterval(i);
  }, [legalPage]);

  useEffect(() => {
    if (legalPage || !SAM_ACTIVITIES.length) return;
    const fire = () => {
      const activity = SAM_ACTIVITIES[Math.floor(Math.random() * SAM_ACTIVITIES.length)];
      setShipFeed((f) => [{ ...activity, id: `sam-${Date.now()}`, ts: Date.now() }, ...f].slice(0, 20));
    };
    fire();
    const i = setInterval(fire, 8000 + Math.floor(Math.random() * 12000));
    return () => clearInterval(i);
  }, [legalPage]);

  const placeBid = useCallback(async ({ url, amount, message, isYou = true }) => {
    api.prewarmHealth();
    setBidBusy(true);
    setBidError("");
    try {
      try {
        sessionStorage.setItem("gmh-pending-url", url);
      } catch {
        /* share CTA can still fall back to a generic tweet */
      }
      const data = await api.checkout({ url, amount, message, isYou });
      window.location.assign(data.url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("checkout failed", e);
      const reason = e.message || "unknown error";
      const serverReject = /floor|invalid|overtake|url/i.test(reason);
      setBidError(
        serverReject
          ? `Checkout failed: ${reason}`
          : production
            ? `Checkout failed: ${reason}. Please try again.`
            : `Checkout failed: ${reason}. Is the backend on :3001 running?`
      );
    } finally {
      setBidBusy(false);
    }
  }, [production]);

  const handleSubmit = (data) => {
    api.prewarmHealth();
    placeBid({ ...data, isYou: true });
  };

  const openBid = useCallback((amount = 0, url = "") => {
    api.prewarmHealth();
    setPrefillAmount(amount);
    setPrefillUrl(url);
    setModalOpen(true);
  }, []);

  // Take #N: open overlay with rank-prefilled amount
  const handleClaim = useCallback((rank) => {
    const amt = takeAmountForRank(listings, rank);
    openBid(amt, "");
  }, [listings, openBid]);

  const handleOpenListing = useCallback((listing) => {
    setListings((ls) => bumpClicks(ls, listing.id));
    window.open(api.goUrl(listing.id), "_blank", "noopener,noreferrer");
  }, []);

  const [showScrollHint, setShowScrollHint] = useState(true);
  useEffect(() => {
    const onScroll = () => setShowScrollHint(window.scrollY < 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (boardState !== "ready") return undefined;
    const jump = () => {
      const id = readListingDeepLink(window.location.hash);
      if (!id) return;
      const el = document.getElementById(`listing-${id}`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };
    jump();
    window.addEventListener("hashchange", jump);
    return () => window.removeEventListener("hashchange", jump);
  }, [boardState, listings]);

  if (confirmId) {
    return (
      <Confirmation
        sessionId={confirmId}
        onPaid={refreshBoard}
        onDismiss={dismissCheckoutReturn}
      />
    );
  }

  if (legalPage) {
    return (
      <div data-testid={`legal-route-${legalPage}`} className="space-bg noise min-h-screen">
        <LegalPage page={legalPage} />
        <Footer />
      </div>
    );
  }

  return (
    <div
      id="top"
      data-testid="app-root"
      className={`space-bg noise min-h-screen ${theme === "light" ? "theme-light light-listings" : ""}`}
    >
      <section data-testid="first-fold">
        <Navbar
          total={total}
          musicOn={musicOn}
          onToggleMusic={toggleMusic}
          onToggleTheme={() => setTheme((t) => (t === "space" ? "light" : "space"))}
          theme={theme === "light" ? "light" : "dark"}
          onOpenBid={() => openBid(0, "")}
        />
        <ShipHud total={total} boost={boost} />

        {/* Fold 1: copy LEFT, gem podium RIGHT on desktop — same max-w-6xl + px as Navbar/HUD.
            URL bar is its own grid node: mobile order copy → podium → URL;
            desktop stacks copy + URL in column 1, podium spans column 2. */}
        <div className="mx-auto mt-5 grid max-w-6xl gap-6 px-4 sm:px-6 lg:grid-cols-[1fr_1.1fr] lg:items-start lg:gap-12 lg:gap-y-4">
          <motion.div
            data-testid="copy-column"
            className="min-w-0 lg:col-start-1 lg:row-start-1"
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <CopyBlock
              visitors={visitors}
              online={online}
            />
          </motion.div>
          <motion.div
            data-testid="podium-column"
            className="mt-5 min-w-0 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:mt-0"
            initial={{ y: 16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.55, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          >
            {boardState === "error" ? (
              <div
                data-testid="podium-offline"
                className="listing-card flex min-w-0 flex-col items-center border border-dashed border-[#26324e] bg-transparent px-4 py-8 text-center"
              >
                <span className="mono text-[10px] tracking-[0.3em] text-slate-500">UPLINK DOWN</span>
                <p className="mt-2 max-w-[16rem] text-sm text-slate-400">
                  Top backers will appear here once the uplink wakes.
                </p>
              </div>
            ) : (
              <PodiumStrip
                leaderboard={leaderboard}
                onOpenListing={handleOpenListing}
                onClaim={handleClaim}
                showGhosts={boardState === "ready"}
              />
            )}
          </motion.div>
          <motion.div
            data-testid="url-column"
            className="min-w-0 max-w-md lg:col-start-1 lg:row-start-2"
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.04, ease: [0.22, 1, 0.36, 1] }}
          >
            <UrlLaunchBar onOpenBid={(url) => openBid(0, url || "")} />
          </motion.div>
        </div>

        {/* Fold 1b: Well Wishers list + LiveFeed */}
        <div className="mx-auto mt-14 grid max-w-6xl gap-6 px-4 sm:px-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          <motion.div
            data-testid="board-column"
            className="min-w-0"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            {listings.length === 0 && boardState === "loading" ? (
              <div data-testid="board-loading" className="listing-card rounded-xl border border-[#1c2740] p-8">
                <div className="mono text-[10px] tracking-[0.3em] text-slate-600">ESTABLISHING UPLINK…</div>
              </div>
            ) : (
              <>
                {boardState === "error" && (
                  <div
                    data-testid="board-error"
                    className="listing-card mb-4 rounded-xl border border-gmh-orange/40 bg-gmh-orange/5 p-6 text-center"
                  >
                    <div className="mono mb-2 text-[10px] tracking-[0.3em] text-gmh-orange">UPLINK DOWN</div>
                    <h2 className="text-lg font-bold text-white">Waking the uplink…</h2>
                    <p className="mx-auto mt-2 max-w-md text-sm text-slate-400">
                      We couldn't reach the mission board. It may be warming up — give it a moment and retry.
                      {boardError ? (
                        <span className="mono mt-2 block text-[11px] text-slate-600">({boardError})</span>
                      ) : null}
                    </p>
                    <button
                      type="button"
                      data-testid="board-retry"
                      onClick={() => refreshBoard()}
                      className="mono mt-4 inline-flex items-center gap-2 rounded-md border border-gmh-cyan/40 bg-gmh-cyan/10 px-4 py-2 text-[11px] font-semibold tracking-wide text-gmh-cyan transition-colors hover:bg-gmh-cyan/20"
                    >
                      Retry
                    </button>
                  </div>
                )}
                {(leaderboard.length > 0 || boardState !== "error") && (
                  <Leaderboard
                    leaderboard={leaderboard}
                    total={total}
                    onOpenListing={handleOpenListing}
                    onClaim={handleClaim}
                    showGhosts={boardState === "ready"}
                    board={listings}
                  />
                )}
              </>
            )}
          </motion.div>
          <motion.div
            data-testid="feed-column"
            className="min-w-0"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <LiveFeed fuel={fuelFeed} traffic={trafficFeed} ship={shipFeed} onOpenListing={handleOpenListing} />
          </motion.div>
        </div>
      </section>

      {/* Marquee with color accents */}
      <div className="mt-16 overflow-hidden border-y border-[#141d34] bg-[#080d18] py-3">
        <div className="marquee-track mono text-xs tracking-[0.4em] text-slate-600">
          <span className="whitespace-nowrap">
            EVERY DOLLAR IS <span className="text-gmh-orange">FUEL</span> — EVERY BID IS <span className="text-gmh-cyan">HOPE</span> — BRING SAM HOME — SECTOR 7 UPLINK ACTIVE —{" "}
          </span>
          <span className="whitespace-nowrap">
            EVERY DOLLAR IS <span className="text-gmh-orange">FUEL</span> — EVERY BID IS <span className="text-gmh-cyan">HOPE</span> — BRING SAM HOME — SECTOR 7 UPLINK ACTIVE —{" "}
          </span>
        </div>
      </div>

      <Hero onOpenBid={() => openBid(0, "")} />

      <Protocol />

      <Footer />

      <BidOverlay
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        board={listings}
        error={bidError}
        busy={bidBusy}
        initialAmount={prefillAmount}
        initialUrl={prefillUrl}
      />

      {showScrollHint && (
        <motion.button
          data-testid="scroll-hint"
          initial={{ opacity: 0, x: "-50%", y: 12 }}
          animate={{ opacity: 1, x: "-50%", y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, delay: 1.2 }}
          onClick={() => document.querySelector('[data-testid="story-below-fold"]')?.scrollIntoView({ behavior: "smooth" })}
          className="mono fixed bottom-5 left-1/2 z-40 flex items-center gap-2 rounded-full border border-gmh-orange/50 bg-[#0b1120]/80 px-5 py-2.5 text-[10px] tracking-[0.25em] text-slate-200 shadow-[0_0_28px_rgba(255,140,70,0.28)] backdrop-blur-md transition-colors hover:border-gmh-orange hover:text-white"
        >
          SCROLL TO LEARN SAM&apos;S STORY
          <svg className="h-3.5 w-3.5 animate-bounce text-gmh-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7" />
          </svg>
        </motion.button>
      )}
    </div>
  );
}

export default App;
